// Borrador de la inspección en curso, en localStorage.
//
// Guarda solo lo que se puede escribir como texto: marcas, notas por ítem,
// comentarios, el paso, la confirmación del PIN, los árboles (ítem fitosanitario)
// y cables (estado del cable, punto de anclaje) elegidos y, en una corrección, el motivo.
// Las fotos y la firma van aparte, en IndexedDB (lib/borradorMedia). Vale solo
// para el día en que se escribió (hora de Costa Rica): uno de otro día se
// descarta. En una corrección, el "día" es la fecha de la inspección corregida.
//
// Un borrador por espacio: la instalación (canopy usa la clave de siempre, el
// Tarzan Swing la suya) o la corrección (clave propia, no pisa la del día).
//
// La clave NO empieza con `cables-pin:` a propósito: cerrar la sesión PIN borra
// ese prefijo, y salir y volver a entrar no debe perder la inspección del día.
// Ningún fallo de localStorage (modo privado, cuota, acceso bloqueado) lanza.
import { ITEM_KEYS, ESTADOS } from 'data/checklist';
import { idsArboles } from 'lib/arbolesCache';

export const CLAVE_BORRADOR = 'cables-borrador-inspeccion:v1';
// Pasos del asistente: 10 ítems + observaciones + firma + resumen.
export const ULTIMO_PASO = ITEM_KEYS.length + 2;

export const claveBorrador = (espacio) =>
  (!espacio || espacio === 'canopy' ? CLAVE_BORRADOR : `${CLAVE_BORRADOR}:${espacio}`);

const esObjeto = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

const limpiar = (crudo, keys) => {
  if (!esObjeto(crudo) || typeof crudo.fecha !== 'string') return null;
  const resultados = {};
  const notas = {};
  const r = esObjeto(crudo.resultados) ? crudo.resultados : {};
  const n = esObjeto(crudo.notas) ? crudo.notas : {};
  keys.forEach((k) => {
    if (ESTADOS.includes(r[k])) resultados[k] = r[k];
    if (typeof n[k] === 'string' && resultados[k] && resultados[k] !== 'aprobado') notas[k] = n[k];
  });
  const paso = Number.isInteger(crudo.paso) ? Math.min(Math.max(crudo.paso, 0), keys.length + 2) : 0;
  const b = {
    fecha: crudo.fecha,
    paso,
    resultados,
    notas,
    comentarios: typeof crudo.comentarios === 'string' ? crudo.comentarios : '',
  };
  // Quién lo empezó y cuándo: en el kiosco se muestra al restaurar, para que
  // otro guía no herede sin aviso la inspección de un compañero.
  if (typeof crudo.inspector === 'string' && crudo.inspector.trim()) b.inspector = crudo.inspector.trim();
  if (Number.isFinite(crudo.iniciadoAt)) b.iniciadoAt = crudo.iniciadoAt;
  if (crudo.confirmado === true) b.confirmado = true;
  if (typeof crudo.motivo === 'string' && crudo.motivo) b.motivo = crudo.motivo;
  if (typeof crudo.corrigeA === 'string' && crudo.corrigeA) b.corrigeA = crudo.corrigeA;
  // Árboles y cables elegidos por ítem (ids), solo en ítems que siguen en
  // Alerta o Falla.
  const porItem = (crudoLista) => {
    const fuente = esObjeto(crudoLista) ? crudoLista : {};
    const out = {};
    keys.forEach((k) => {
      const ids = idsArboles(fuente[k]);
      if (ids.length && resultados[k] && resultados[k] !== 'aprobado') out[k] = ids;
    });
    return out;
  };
  const arboles = porItem(crudo.arboles);
  if (Object.keys(arboles).length) b.arboles = arboles;
  const lineas = porItem(crudo.lineas);
  if (Object.keys(lineas).length) b.lineas = lineas;
  return b;
};

const idsPorItem = (v) => (esObjeto(v)
  ? Object.fromEntries(Object.entries(v).map(([k, ids]) => [k, idsArboles(ids)]).filter(([, ids]) => ids.length))
  : {});

export const borrarBorrador = (espacio) => {
  try {
    localStorage.removeItem(claveBorrador(espacio));
  } catch (e) {
    /* sin almacenamiento: no hay nada que borrar */
  }
};

export const guardarBorrador = (
  { fecha, paso, resultados, notas, comentarios, inspector, iniciadoAt, confirmado, motivo, corrigeA, arboles, lineas },
  espacio,
) => {
  const arb = idsPorItem(arboles);
  const lin = idsPorItem(lineas);
  try {
    localStorage.setItem(
      claveBorrador(espacio),
      JSON.stringify({
        fecha, paso, resultados, notas, comentarios,
        inspector: inspector || undefined,
        iniciadoAt,
        confirmado: confirmado === true ? true : undefined,
        motivo: motivo || undefined,
        corrigeA: corrigeA || undefined,
        arboles: Object.keys(arb).length ? arb : undefined,
        lineas: Object.keys(lin).length ? lin : undefined,
      }),
    );
  } catch (e) {
    /* sin almacenamiento o sin cuota: la inspección sigue en memoria */
  }
};

// Devuelve el borrador de `hoy` o null. Uno de otro día se borra.
//   espacio   instalación o 'correccion' (sin espacio: canopy)
//   keys      claves válidas del checklist de ese espacio (por defecto canopy)
//   corrigeA  en una corrección, el id corregido: uno de otra inspección se borra
export const leerBorrador = (hoy, { espacio, keys = ITEM_KEYS, corrigeA } = {}) => {
  let crudo;
  try {
    const texto = localStorage.getItem(claveBorrador(espacio));
    if (!texto) return null;
    crudo = JSON.parse(texto);
  } catch (e) {
    return null;
  }
  const b = limpiar(crudo, keys);
  if (!b || b.fecha !== hoy || (corrigeA !== undefined && b.corrigeA !== corrigeA)) {
    borrarBorrador(espacio);
    return null;
  }
  return b;
};
